import axios from 'axios';

const API_BASE = import.meta.env.PROD 
  ? 'https://cineclubs.cloud' 
  : 'http://localhost:3001';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Stream API methods
 * Uses Node.js server endpoints
 */
export const streamApi = {
  // Get all active streams
  getActiveStreams: () => 
    api.get('/api/streams/active', { withCredentials: true }),
  
  // Get all streams (live + recent)
  getAllStreams: () => 
    api.get('/api/streams', { withCredentials: true }),
  
  // Get stream status
  getStatus: (slug) => 
    api.get(`/api/stream/${slug}/status`, { withCredentials: true }),
  
  // Start streaming
  start: (title) => 
    api.post('/api/stream/start', { title }, { withCredentials: true }),
  
  // Stop streaming
  stop: () => 
    api.post('/api/stream/stop', {}, { withCredentials: true }),
  
  // Get LiveKit token (use PHP endpoint which has working credentials)
  getLivekitToken: (room, publisher = false) => 
    api.post('/api/livekit-token.php', { room, identity: '', publisher }, { withCredentials: true }),
  
  // Get chat messages
  getChat: (slug, limit = 50) => 
    api.get(`/api/stream/${slug}/chat?limit=${limit}`, { withCredentials: true }),
  
  // Get chat messages (alias)
  getChatMessages: (slug, limit = 100) => 
    api.get(`/api/stream/${slug}/chat?limit=${limit}`, { withCredentials: true }),
  
  // Send chat message
  sendChat: (slug, message) => 
    api.post(`/api/stream/${slug}/chat`, { message }, { withCredentials: true }),
  
  // Update stream settings (title, category, thumbnail)
  updateSettings: (settings) => 
    api.post('/api/stream/settings', settings, { withCredentials: true }),
  
  // Get stream key
  getStreamKey: () => 
    api.get('/api/stream/key', { withCredentials: true }),
  
  // Generate new stream key
  generateStreamKey: () => 
    api.post('/api/stream/key/generate', {}, { withCredentials: true }),
  
  // Get dashboard data
  getDashboard: () => 
    api.get('/api/stream/dashboard', { withCredentials: true }),
  
  // Upload thumbnail (uses PHP endpoint on main server)
  uploadThumbnail: async (file) => {
    const formData = new FormData();
    formData.append('thumbnail', file);
    
    // Use direct URL to PHP endpoint on main server (not Node server)
    const phpUrl = import.meta.env.PROD 
      ? 'https://cineclubs.cloud/api/stream/thumbnail.php'
      : 'http://localhost/api/stream/thumbnail.php';
    
    const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
    
    return fetch(phpUrl, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {}
    }).then(res => res.json()).then(data => ({ data }));
  }
};

/**
 * User API methods
 */
export const userApi = {
  // Get current user
  getMe: () => 
    api.get('/api/auth/me'),
  
  // Get user by ID
  getUser: (id) => 
    api.get(`/api/users/${id}`)
};

export default api;
